import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormHelperText, makeStyles } from '@material-ui/core';
import VirtualizedList from '../virtualizedList';
import VirtualSelectedUsers from '../virtualSelectedUsers';
import NotificationCard from '../notificationCard';
import { ReactComponent as UsersIcon } from '../../../assets/icons/people_alt-icon.svg';
import { isArrayEmpty, replaceObjectInList } from '../../../utility/helpers';
import { useElementMaxHeight } from '../../../utility/hooks';

const useStyles = makeStyles(({ palette: { primary } }) => ({
  selectedItemWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  selectedItemsViewport: {
    maxHeight: ({ shouldCalculateSelectionHeight, selectionMaxHeight }) =>
      shouldCalculateSelectionHeight ? selectionMaxHeight : 528,
  },
  selectedItemsWrapper: {
    flex: 1,
  },
  selectedItemsScrollY: {
    right: -15,
  },
}));

const ManageUsersList = ({
  parentNode,
  parentOffset = 0,
  className = undefined,
  customErrorClass = undefined,
  selectedItemsScrollYClass = undefined,
  translations = {},
  values = [],
  onSelect,
  hasError = false,
  hasDelete = false,
  errorMessage = '',
  showSelection = false,
  shouldCalculateSelectionHeight = false,
  shouldHandleTypeLabel = false,
  handleTypeLabel = () => {},
}) => {
  const [setElementRef, selectionMaxHeight] = useElementMaxHeight(
    parentNode,
    !hasError &&
      showSelection &&
      shouldCalculateSelectionHeight &&
      !isArrayEmpty(values),
    parentOffset
  );
  const classes = useStyles({
    selectionMaxHeight,
    shouldCalculateSelectionHeight,
  });

  const getPreselectedVirtualItemSize = useCallback(() => 40, []);

  const handleDeselectItem = useCallback(
    (item, index) => {
      const updatedList = replaceObjectInList(values, index, {
        ...item,
        isRemoved: true,
      });
      onSelect(updatedList);
    },
    [onSelect, values]
  );

  const handleRevertItem = useCallback(
    (item, index) => {
      const updatedList = replaceObjectInList(values, index, {
        ...item,
        isRemoved: false,
      });
      onSelect(updatedList);
    },
    [onSelect, values]
  );

  const renderItem = useCallback(
    (previewedItem, item) => {
      const typeLabel = shouldHandleTypeLabel
        ? handleTypeLabel(previewedItem)
        : false;

      return (
        <VirtualSelectedUsers
          translations={translations}
          item={previewedItem}
          typeLabel={typeLabel || ''}
          shouldHandleTypeLabel={shouldHandleTypeLabel}
          onDeselect={record => handleDeselectItem(record, item.index)}
          onRevert={record => handleRevertItem(record, item.index)}
          isRemoved={previewedItem.isRemoved}
          hasDelete={hasDelete}
        />
      );
    },
    [
      translations,
      handleTypeLabel,
      shouldHandleTypeLabel,
      handleDeselectItem,
      handleRevertItem,
      hasDelete,
    ]
  );

  return (
    <div className={className}>
      {hasError && errorMessage && (
        <FormHelperText className={customErrorClass} error={hasError}>
          {errorMessage}
        </FormHelperText>
      )}
      {showSelection && !isArrayEmpty(values) && (
        <div ref={setElementRef} className={classes.selectedItemsWrapper}>
          <VirtualizedList
            viewportClass={classes.selectedItemsViewport}
            verticalScrollClass={classNames(
              classes.selectedItemsScrollY,
              selectedItemsScrollYClass
            )}
            itemClass={classes.selectedItemWrapper}
            items={values}
            count={values?.length ?? 0}
            gap={16}
            estimateSize={getPreselectedVirtualItemSize}
            renderItem={renderItem}
          />
        </div>
      )}
      <NotificationCard
        message={translations.noUsers}
        customIcon={UsersIcon}
        shouldFade={showSelection && isArrayEmpty(values)}
        hasIcon
      />
    </div>
  );
};

ManageUsersList.propTypes = {
  className: PropTypes.string,
  customErrorClass: PropTypes.string,
  selectedItemsScrollYClass: PropTypes.string,
  translations: PropTypes.object,
  values: PropTypes.arrayOf(PropTypes.shape({})),
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  parentOffset: PropTypes.number,
  showSelection: PropTypes.bool,
  hasDelete: PropTypes.bool,
  shouldCalculateSelectionHeight: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  shouldHandleTypeLabel: PropTypes.bool,
  handleTypeLabel: PropTypes.func,
};

export default ManageUsersList;
